import { weightedPickPose } from "./poseUtils";

/** Fisher–Yates shuffle (копия массива) */
function shuffleArray(list) {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function sampleN(list, n) {
  return shuffleArray(list).slice(0, n);
}

/** Display label for an asana name: Russian translation in Russian mode, Sanskrit + English otherwise. */
function poseNameLabel(pose, language) {
  return language === "russian" ? pose.russian : `${pose.sanskrit} (${pose.english})`;
}

/** "Как называется эта асана?" — picture with 4 name options. */
export function buildNameQuestion(poses, pose, language = "russian") {
  const others = poses.filter((p) => p !== pose && p.sanskrit !== pose.sanskrit);
  const distractors = sampleN(others, Math.min(3, others.length));
  const options = shuffleArray([pose, ...distractors]).map((p) => ({
    key: p.sanskrit,
    label: poseNameLabel(p, language),
  }));
  return { type: "name", pose, options, answer: { key: pose.sanskrit, label: poseNameLabel(pose, language) } };
}

/** "Найди картинку" — name prompt with 4 picture options. */
export function buildPictureQuestion(poses, pose, language = "russian") {
  const others = poses.filter((p) => p !== pose && p.sanskrit !== pose.sanskrit);
  const distractors = sampleN(others, Math.min(3, others.length));
  const options = shuffleArray([pose, ...distractors]);
  return { type: "picture", pose, options, prompt: poseNameLabel(pose, language), answer: pose.sanskrit };
}

function rootMeaning(item, language) {
  const langCode = language === "english" ? "en" : "ru";
  return item.translations[langCode].meaning;
}

/** Sanskrit root as displayed to the reader: Cyrillic transliteration in Russian mode, Latin spelling otherwise. */
function rootLabel(item, language) {
  return language === "russian" ? item.transliteration : item.sk;
}

/**
 * Finds a pose's root word (from Pose.roots, e.g. "tri") in the Sanskrit roots glossary.
 * Glossary "sk" entries can list display alternates like "dvi / dwi", and an item can also carry
 * an "aliases" list of other spellings that match but aren't shown (e.g. "vrksa" for "vriksa").
 */
function findGlossaryRoot(rootWord, rootItems) {
  const target = (rootWord || "").trim().toLowerCase();
  return rootItems.find(
    (r) =>
      r.sk.split("/").some((alt) => alt.trim().toLowerCase() === target) ||
      (r.aliases || []).some((alt) => alt.trim().toLowerCase() === target)
  );
}

/** Cyrillic transliteration in Russian mode, Latin spelling otherwise. Falls back to the raw root word when there's no glossary match. */
export function poseRootLabel(rootWord, rootItems = [], language = "russian") {
  const match = findGlossaryRoot(rootWord, rootItems);
  return match ? rootLabel(match, language) : rootWord;
}

/** Root meaning translated into the current language via the glossary. Falls back to the pose's stored (Russian) meaning when there's no glossary match. */
export function poseRootMeaning(rootWord, meaning, rootItems = [], language = "russian") {
  const match = findGlossaryRoot(rootWord, rootItems);
  return match ? rootMeaning(match, language) : meaning;
}

/** "Что означает этот корень?" — Sanskrit root shown, 4 meaning options in the current language. */
export function buildRootToMeaningQuestion(rootItems, item, language = "russian") {
  const others = rootItems.filter((r) => r !== item && rootMeaning(r, language) !== rootMeaning(item, language));
  const distractors = sampleN(others, Math.min(3, others.length));
  const options = shuffleArray([item, ...distractors]).map((r) => rootMeaning(r, language));
  return { type: "root-meaning", prompt: rootLabel(item, language), options, answer: rootMeaning(item, language) };
}

/** "Какой корень означает это?" — meaning shown in the current language, 4 Sanskrit root options. */
export function buildMeaningToRootQuestion(rootItems, item, language = "russian") {
  const others = rootItems.filter((r) => r !== item && r.sk !== item.sk);
  const distractors = sampleN(others, Math.min(3, others.length));
  const options = shuffleArray([item, ...distractors]).map((r) => rootLabel(r, language));
  return { type: "root-from-meaning", prompt: rootMeaning(item, language), options, answer: rootLabel(item, language) };
}

export function generateRootQuestion(rootItems, language = "russian") {
  const item = rootItems[Math.floor(Math.random() * rootItems.length)];
  return Math.random() < 0.5
    ? buildRootToMeaningQuestion(rootItems, item, language)
    : buildMeaningToRootQuestion(rootItems, item, language);
}

export function generateQuestion(poses, typeFilter = "mixed", language = "russian", progressBySanskrit = {}, rootItems = []) {
  const rootsAvailable = rootItems.length >= 4;
  let type = typeFilter;
  if (type === "mixed") {
    const types = rootsAvailable ? ["name", "picture", "roots"] : ["name", "picture"];
    type = types[Math.floor(Math.random() * types.length)];
  }
  if (type === "roots") {
    if (!rootsAvailable) {
      type = Math.random() < 0.5 ? "picture" : "name";
    } else {
      return generateRootQuestion(rootItems, language);
    }
  }
  const pose = weightedPickPose(poses, progressBySanskrit);
  if (type === "picture") {
    return buildPictureQuestion(poses, pose, language);
  }
  return buildNameQuestion(poses, pose, language);
}

export function generateExamQuestions(poses, count = 20, language = "russian", progressBySanskrit = {}, rootItems = []) {
  return Array.from({ length: count }, () =>
    generateQuestion(poses, "mixed", language, progressBySanskrit, rootItems)
  );
}
